export interface HavingCondition {
  field: string;
  operator: string;
  value: any;
  logicalOperator?: "AND" | "OR";
}

export const AGGREGATE_FUNCTIONS = [
  { value: "COUNT", label: "COUNT" },
  { value: "SUM", label: "SUM" },
  { value: "AVG", label: "AVG" },
  { value: "MAX", label: "MAX" },
  { value: "MIN", label: "MIN" },
  { value: "GROUP_CONCAT", label: "GROUP_CONCAT" },
];

export const OPERATORS = [
  { value: "=", label: "operatorEquals" },
  { value: "!=", label: "operatorNotEquals" },
  { value: ">", label: "operatorGreater" },
  { value: "<", label: "operatorLess" },
  { value: ">=", label: "operatorGreaterOrEqual" },
  { value: "<=", label: "operatorLessOrEqual" },
  { value: "BETWEEN", label: "operatorBetween" },
  { value: "IN", label: "operatorIn" },
  { value: "NOT IN", label: "operatorNotIn" },
  { value: "LIKE", label: "operatorLike" },
  { value: "NOT LIKE", label: "operatorNotLike" },
  { value: "IS NULL", label: "operatorIsNull" },
  { value: "IS NOT NULL", label: "operatorIsNotNull" },
];

export const LOGICAL_OPERATORS = [
  { value: "AND", label: "logicalAnd" },
  { value: "OR", label: "logicalOr" },
];

export function parseHaving(sql: string): HavingCondition[] {
  if (!sql || sql.trim() === "") return [];

  try {
    const conditions: HavingCondition[] = [];
    
    const parts = sql.split(/\s+(AND|OR)\s+/i);
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      
      if (part === "AND" || part === "OR") {
        if (conditions.length > 0) {
          conditions[conditions.length - 1].logicalOperator = part.toUpperCase() as "AND" | "OR";
        }
        continue;
      }

      let match;
      
      if ((match = part.match(/(\w+)\((\w+)\)\s+BETWEEN\s+(.+)\s+AND\s+(.+)/i))) {
        const [, func, field, val1, val2] = match;
        conditions.push({
          field: `${func}(${field})`,
          operator: "BETWEEN",
          value: `${val1.trim()} AND ${val2.trim()}`,
        });
      }
      else if ((match = part.match(/(\w+)\((\w+)\)\s+(NOT\s+)?IN\s*\((.+)\)/i))) {
        const [, func, field, notIn, values] = match;
        conditions.push({
          field: `${func}(${field})`,
          operator: notIn ? "NOT IN" : "IN",
          value: values.trim(),
        });
      }
      else if ((match = part.match(/(\w+)\((\w+)\)\s+IS\s+(NOT\s+)?NULL/i))) {
        const [, func, field, notNull] = match;
        conditions.push({
          field: `${func}(${field})`,
          operator: notNull ? "IS NOT NULL" : "IS NULL",
          value: "",
        });
      }
      else if ((match = part.match(/(\w+)\((\w+)\)\s+(NOT\s+)?LIKE\s+(.+)/i))) {
        const [, func, field, notLike, val] = match;
        conditions.push({
          field: `${func}(${field})`,
          operator: notLike ? "NOT LIKE" : "LIKE",
          value: val.trim().replace(/^['"]|['"]$/g, ""),
        });
      }
      else if ((match = part.match(/(\w+)\((\w+)\)\s*([><=!]+)\s*(.+)/))) {
        const [, func, field, operator, val] = match;
        conditions.push({
          field: `${func}(${field})`,
          operator: operator.trim(),
          value: val.trim(),
        });
      }
    }

    return conditions.length > 0
      ? conditions
      : [{ field: "", operator: "=", value: "" }];
  } catch (err) {
    console.error("Parse error:", err);
    return [{ field: "", operator: "=", value: "" }];
  }
}

export function conditionsToSQL(conditions: HavingCondition[]): string {
  return conditions
    .filter((c) => {
      if (c.operator === "IS NULL" || c.operator === "IS NOT NULL") {
        return c.field;
      }
      return c.field && c.operator && c.value;
    })
    .map((c, index) => {
      let sql = "";
      
      if (c.operator === "BETWEEN") {
        sql = `${c.field} BETWEEN ${c.value}`;
      } else if (c.operator === "IN" || c.operator === "NOT IN") {
        sql = `${c.field} ${c.operator} (${c.value})`;
      } else if (c.operator === "IS NULL" || c.operator === "IS NOT NULL") {
        sql = `${c.field} ${c.operator}`;
      } else if (c.operator === "LIKE" || c.operator === "NOT LIKE") {
        sql = `${c.field} ${c.operator} '${c.value}'`;
      } else {
        sql = `${c.field} ${c.operator} ${c.value}`;
      }
      
      if (index < conditions.length - 1 && c.logicalOperator) {
        sql += ` ${c.logicalOperator}`;
      }
      
      return sql;
    })
    .join(" ");
}

export function isValueRequired(operator: string): boolean {
  return operator !== "IS NULL" && operator !== "IS NOT NULL";
}

export function isSpecialOperator(operator: string): boolean {
  return [
    "BETWEEN",
    "IN",
    "NOT IN",
    "IS NULL",
    "IS NOT NULL",
  ].includes(operator);
}

export function extractFunction(field: string): string {
  const match = field.match(/^(\w+)\(/);
  return match ? match[1] : "COUNT";
}

export function extractFieldName(field: string): string {
  const match = field.match(/\((\w+)\)/);
  return match ? match[1] : "";
}

export function createFieldString(func: string, fieldName: string): string {
  return `${func}(${fieldName})`;
}